import { decamelizeDeep } from '@vpr/restful';

import {
  BigPictureClient,
  Event,
  EventContexts,
  Events,
  Fields,
  Versions,
} from '@vimeo/bigpicture-client';
import { AnyObject, Callable, Maybe, Subset } from '@vimeo/core/types/generic';
import { Logger } from '@vimeo/logging';
import { sendBpEventWithContexts, WebBpContext } from '@vimeo/use-tracking';

import { environmentConfig } from '../../configs/EnvironmentConfig';
import { liveApplicationConfig } from '../../configs/LiveApplicationConfig';
import { ILiveTrackingConfig, liveTrackingConfig } from '../../configs/LiveTrackingConfig';

const log: Logger = new Logger('👁BP', liveApplicationConfig.TRACKING.IS_BP_LOG_ENABLED);

export type TSchemaEventFactory = (eventName: string, additionalData?: AnyObject) => void;
export type TSchemaEventFactoryNew = (additionalData?: AnyObject) => void;
export type TSchemaV2EventFactory = (additionalData?: AnyObject) => void;

// todo: Tests for utils.

/**
 * Update tracking config based on provided object subset.
 */
export function updateTrackingConfig(
  config: Subset<ILiveTrackingConfig>,
  destination: ILiveTrackingConfig = liveTrackingConfig,
): void {
  for (const key in config) {
    if (typeof destination[key] === 'object') {
      Object.assign(destination[key], config[key]);
    }
  }
}

/**
 * Check if client is initialized.
 */
export function isBPInitialized(): boolean {
  return BigPictureClient['isInitalized'] !== false;
}

/**
 * Check whether bp tracking config is ready for processing of data.
 */
export function isLiveTrackingConfigReady({
  BIG_PICTURE_LIVE_SCHEMA_BASE: { liveEventId, liveEventType },
}: ILiveTrackingConfig = liveTrackingConfig): boolean {
  return isBPInitialized() && Boolean(liveEventId && liveEventType);
}

/**
 * Check whether bp tracking config is ready for processing of data in interaction app.
 */
export function isInteractionTrackingConfigReady({
  BIG_PICTURE_INTERACTION_SCHEMA_BASE: { roomId, roomType },
}: ILiveTrackingConfig = liveTrackingConfig): boolean {
  return isBPInitialized() && Boolean(roomId && roomType);
}

/**
 * Create event factory for specified schema / version / fields.
 */
export function createLiveSchemaEventFactory<T extends AnyObject = AnyObject>(
  schemaName: Events,
  schemaVersion: number,
  shared: T | Callable<T>,
  nameField = 'name',
): TSchemaEventFactory {
  return (eventName, additional = {}) => {
    return sendLiveTrackingEvent(
      new Event(
        schemaName,
        /* @ts-expect-error It would require too much refactoring to benefit from big picture's new built-in type safety. Deferring to the live team on whether that's worth doing. */
        schemaVersion,
        decamelizeDeep({
          eventType: 'click',
          target: null,
          copy: null,
          page: null,
          location: '',
          // Field name optional.
          ...(eventName ? { [nameField]: eventName } : {}),
          // Pick scheme shared fields based on factory or constant ref template.
          ...(typeof shared === 'function' ? (shared as Callable)() : shared),
          // Pick event specific field additions.
          ...additional,
        }),
      ),
    );
  };
}

export type Contexts = EventContexts[Events];

export function createBPv2EventFactory<T extends AnyObject = AnyObject>(
  schemaName: Events,
  schemaVersion: number,
  contexts: Callable<Contexts>,
  shared: T | Callable<T>,
): TSchemaV2EventFactory {
  return (additional: Maybe<AnyObject> = {}) => {
    // DO NOT track on explicit disable and in testing environments etc.
    if (!canTrackBPEvents()) {
      return;
    }

    const eventContexts: Contexts = contexts();
    const eventData: Fields<Events, Versions<Events>> = {
      ...additional,
      // Pick scheme shared fields based on factory or constant ref template.
      ...(typeof shared === 'function' ? (shared as Callable)() : shared),
    } as Fields<Events, Versions<Events>>;

    log.info('Sending v2 event:', schemaName, schemaVersion, eventContexts, eventData);

    return sendBpEventWithContexts(
      schemaName,
      eventContexts,
      schemaVersion as Versions<Events>,
      eventData,
    );
  };
}

/**
 * Create event factory for specified schema / version / fields.
 */
export function createLiveSchemaEventFactoryNew<T extends AnyObject = AnyObject>(
  schemaName: Events,
  schemaVersion: number,
  shared: T | Callable<T>,
): TSchemaEventFactoryNew {
  return (additional = {}) => {
    return sendLiveTrackingEvent(
      new Event(
        schemaName,
        /* @ts-expect-error It would require too much refactoring to benefit from big picture's new built-in type safety. Deferring to the live team on whether that's worth doing. */
        schemaVersion,
        decamelizeDeep({
          eventType: 'click',
          target: null,
          copy: null,
          page: null,
          location: '',
          // Pick scheme shared fields based on factory or constant ref template.
          ...(typeof shared === 'function' ? (shared as Callable)() : shared),
          // Pick event specific field additions.
          ...additional,
        }),
      ),
    );
  };
}

/**
 * Check whether BP event can be tracked.
 *  - Tracking is enabled
 *  - Not in ignored users list
 *  - Is not in test/jest ENV
 */
export function canTrackBPEvents(): boolean {
  return (
    !environmentConfig.IS_TEST &&
    liveApplicationConfig.TRACKING.IS_ENABLED &&
    !liveApplicationConfig.TRACKING.BP_IGNORED_USERS.includes(
      liveTrackingConfig.DETAILS.USER_ID as number,
    )
  );
}

/**
 * Send BP tracking event if tracking is enabled.
 * Logs information if logger is enabled.
 */
export function sendLiveTrackingEvent<E extends Events, V extends Versions<E>>(
  event: Event<E, V>,
): void {
  if (!canTrackBPEvents()) {
    return;
  }

  if (isBPInitialized()) {
    log.info('Sending event:', event);
    BigPictureClient.sendEvent(event);
  } else {
    log.warn('Tracker was not ready when tried to send event:', event);
  }
}

/**
 * Parses url and returns page_nane / referrer_page_name according to Web Context
 */
export function pageNameFromPathname(pathname: string): WebBpContext['page_name'] {
  let pageName: WebBpContext['page_name'] = 'other';

  if (pathname.match(/\/live\/broadcaster(\/event)?\/[0-9]+.*/)) {
    pageName = 'live_browser_studio';
  } else if (pathname.match(/\/live\/(rtmp|completed)\/event\/[0-9]+.*/)) {
    pageName = 'live_event_rtmp_page';
  } else if (
    pathname.match(/\/events?\/[0-9]+.*/) ||
    pathname.match(/\/live\/(guest\/)?([0-9a-zA-Z])?(\/)?.*/)
  ) {
    pageName = 'live_event_view_page';
  } else if (pathname.match(/^\/upload(\/.*)?$/)) {
    pageName = 'upload_page';
  } else if (pathname.match(/\/manage\/folders/)) {
    pageName = 'video_library';
  } else if (pathname.match(/^\/(home)?/)) {
    pageName = 'self_serve_logged_in_homepage';
  }

  const libraryMatch = pathname.match(/\/manage\/(videos|events|showcases)/);
  if (libraryMatch) {
    switch (libraryMatch[1]) {
      case 'videos':
        pageName = 'video_library';
        break;
      case 'showcases':
        pageName = 'showcases_library';
        break;
      case 'events':
        pageName = 'event_library';
        break;
      default:
        pageName = 'other';
        break;
    }
  }

  return pageName;
}
